/**
 * Discrete game state: the phase the session is in, and the player's settings.
 *
 * This store is for things that change on *events*, never per frame. Positions,
 * velocities and input live elsewhere — the physics world owns anything spatial, and
 * `src/input/inputState.ts` owns per-frame input, deliberately outside React. Writing
 * per-frame data here would re-render the tree every frame and destroy the frame budget.
 * See CLAUDE.md, architectural rule 3.
 */

import { create } from 'zustand';
import {
  attachedTo,
  ASSEMBLIES,
  canAttach,
  isComplete,
  nextSocket,
  type Attachments,
} from '../game/assembly';
import { toggleDoor } from '../game/doors';
import type { DoorState, FocusTarget, LightState } from '../game/interaction';
import {
  applyPlayerEnterZone,
  applyZoneEvent,
  initialObjectiveProgress,
  type ObjectiveProgress,
  type ZoneEvent,
} from '../game/objectives';
import { applyPhaseEvent, INITIAL_PHASE, type Phase, type PhaseEvent } from '../game/phase';
import { seedObjectives } from '../game/objectives';
import { firstLevel, levelById, type ArriveAt } from '../game/progression';
import { DEFAULT_SETTINGS, parseSettings, type Settings } from '../game/settings';
import type { ObjectKind, Profile } from '../game/profile/types';
import { isStowable } from '../game/objectKinds';
import { emptyPocket, hasPocket, stowInPocket, takeFromSlot, type Pocket } from '../game/pocket';
import { loadSettings, saveSettings } from '../storage/persistence';

type GameStore = {
  phase: Phase;
  settings: Settings;
  /** The deploy's validated profile. Set once at boot, before LOADED; never changes. */
  profile: Profile | null;
  /**
   * The level being played. A run visits one level at a time — only this one is built,
   * and everything else in the profile is content that is not in the world right now.
   */
  currentLevelId: string | null;
  /** Which end of the current level the player is standing at. */
  arriveAt: ArriveAt;
  /**
   * Where a crossing in flight is headed, held until the fade has covered the screen.
   * Null whenever nothing is crossing. Nothing spatial: which level and which end, and
   * the maze works out where that is in metres.
   */
  pendingArrival: { levelId: string; arriveAt: ArriveAt } | null;
  /** What the camera rests on within reach. Written only when the target changes. */
  focus: FocusTarget | null;
  doorStates: Record<string, DoorState>;
  /** Whether each room's lamp is on, keyed by room id. Rooms start lit. */
  roomLights: Record<string, LightState>;
  /** How many bodies (player, boxes) the doorway sensor currently sees, per door. */
  doorwayOccupancy: Record<string, number>;
  carriedObjectId: string | null;
  /** The kind of the carried object, alongside its id — null with empty hands. */
  carriedObjectKind: ObjectKind | null;
  /**
   * The table the player currently has hold of, or null. Distinct from `carriedObjectId`
   * because a drag is not a carry: the object stays on the ground, keeps its own body, and
   * is let go the instant the hold ends.
   */
  draggedObjectId: string | null;
  /** Three slots for small items, in order. Belongs to the level, like the hands. */
  pocket: Pocket;
  /**
   * A transient message the player has to see — "Pocket full", "Leg attached — 2 of 4",
   * "Robot dog complete". One channel for every feature that has something brief to say
   * (design D9): the HUD shows it and calls `clearItemMessage` a couple of seconds later.
   */
  itemMessage: string | null;
  /** Every attached robot-dog part in the run, keyed by the part's object id. */
  attachments: Attachments;
  /**
   * The id of the one host whose assembly is complete, or null (design D3). Set inside
   * `attachPart` on the frame `isComplete` first answers true, and cleared by `restart`
   * and by `arrive` alongside the attachments it is derived from. A discrete fact about a
   * run — read by `RobotDogBody` to ask "am I the one walking" and by `FrozenShadows` to
   * ask "has the caster set changed" — not a per-frame derivation of `attachments` (see
   * D3's rejected alternative).
   */
  followerId: string | null;
  objectiveProgress: Record<string, ObjectiveProgress>;
  /** A newer build has finished downloading and is waiting for the player to take it. */
  isUpdateReady: boolean;
  /** Reloads onto the waiting build. Null until one is ready. See src/pwa/. */
  applyUpdate: (() => void) | null;
  /** Advance the phase machine. Events that do not apply are ignored. */
  dispatch: (event: PhaseEvent) => void;
  /** Apply a settings change immediately, then persist it. */
  updateSettings: (patch: Partial<Settings>) => Promise<void>;
  /** Load persisted settings. Called once at boot, before the first frame. */
  hydrate: () => Promise<void>;
  setProfile: (profile: Profile) => void;
  setFocus: (focus: FocusTarget | null) => void;
  /** The interact action on a door. A close is refused while the doorway is occupied. */
  toggleDoor: (doorId: string) => void;
  /** Flip one room's lamp. A room the profile does not define is ignored. */
  toggleRoomLight: (roomId: string) => void;
  /** Take hold of a table. Refused while carrying, or while another drag is in flight. */
  beginDrag: (objectId: string) => void;
  /** Let go of the dragged table. A no-op when nothing is being dragged. */
  endDrag: () => void;
  /** A closing door found something in the doorway mid-swing and reverses. */
  forceDoorOpen: (doorId: string) => void;
  reportDoorwayEntered: (doorId: string) => void;
  reportDoorwayLeft: (doorId: string) => void;
  /**
   * Ignored while already carrying — one object at a time. Takes the kind alongside the
   * id: the store must not keep a second copy of the level's contents to look it up
   * later (the rule `pocket-inventory` set), so whoever calls this already knows it.
   */
  pickUp: (objectId: string, kind: ObjectKind | null) => void;
  /** Empty the hands; thrown or dropped is the engine's concern. */
  release: () => void;
  /**
   * Put the held item in the pocket. The caller says what kind it is — the engine mounts
   * the objects and knows, and this store must not hold a second copy of the level's
   * contents to answer it. Refused with empty hands, with a bulky kind, or when the
   * pocket is full, which is the one refusal that leaves a message.
   */
  stow: (kind: ObjectKind) => void;
  /** Take a slot's item back into the hands. Refused with full hands or an empty slot. */
  takeFromPocket: (slot: number) => void;
  /** The message has been shown for long enough. */
  clearItemMessage: () => void;
  /**
   * Attach whatever is in the player's hands to a host's next free socket (design D3).
   * The part is always the carried object — its id and kind are already in the store —
   * so only the host needs naming here. Delegates every rule to `src/game/assembly.ts`;
   * refused silently (no state change) when the carried kind does not fit the host or the
   * host has no socket free. Writes the running count, or the completion message on the
   * host's last socket.
   */
  attachPart: (hostId: string, hostKind: ObjectKind) => void;
  /** A drop-zone sensor saw a required object enter or leave. */
  reportZoneEvent: (objectiveId: string, event: ZoneEvent) => void;
  /** The maze's exit sensor saw the player. Completes the objective, and latches. */
  reachExit: (objectiveId: string) => void;
  /**
   * The player walked into a gate. Records where it leads and stops the world; the swap
   * itself happens on `arrive`, once the fade has covered it. Ignored unless the game is
   * being played, which is what keeps a crossing from starting twice.
   */
  beginCrossing: (destination: { levelId: string; arriveAt: ArriveAt }) => void;
  /** The fade has covered the screen: swap the level in and hand control back. */
  arrive: () => void;
  /** Run the whole thing again from the first level, with nothing completed. */
  restart: () => void;
  /** A newer build is waiting. Called from a service-worker callback, never per frame. */
  markUpdateReady: (applyUpdate: () => void) => void;
};

/**
 * Everything a run starts with: the first level, and the doors, lights and objectives of
 * every level in the profile as the profile authored them. From then on the store owns
 * them.
 *
 * Seeded for *all* levels, not just the one being played. These are a handful of plain
 * records — not the memory a level swap is about — and seeding them once is what lets an
 * objective stay completed when the player crosses back into a level they have already
 * finished. `validate.ts` refuses a profile that uses an id twice, so one flat record per
 * kind is unambiguous.
 */
function freshRun(profile: Profile) {
  const doorStates: Record<string, DoorState> = {};
  const objectiveProgress: Record<string, ObjectiveProgress> = {};
  // Every room starts lit: a player should never arrive in a house that is dark by
  // accident, only one they have darkened themselves.
  const roomLights: Record<string, LightState> = {};

  for (const level of profile.levels) {
    for (const door of level.doors) doorStates[door.id] = door.isInitiallyOpen ? 'open' : 'closed';
    for (const objective of level.objectives) {
      objectiveProgress[objective.id] = initialObjectiveProgress(objective);
    }
    for (const room of level.rooms) roomLights[room.id] = 'on';
  }

  const { levelId, arriveAt } = firstLevel(profile);
  return {
    doorStates,
    roomLights,
    objectiveProgress,
    currentLevelId: levelId,
    arriveAt,
    pendingArrival: null,
  };
}

export const useGameStore = create<GameStore>((set, get) => ({
  phase: INITIAL_PHASE,
  settings: { ...DEFAULT_SETTINGS },
  profile: null,
  currentLevelId: null,
  arriveAt: 'entry',
  pendingArrival: null,
  focus: null,
  doorStates: {},
  roomLights: {},
  doorwayOccupancy: {},
  carriedObjectId: null,
  carriedObjectKind: null,
  draggedObjectId: null,
  pocket: emptyPocket(),
  itemMessage: null,
  attachments: {},
  followerId: null,
  objectiveProgress: {},
  isUpdateReady: false,
  applyUpdate: null,

  dispatch: (event) => {
    const next = applyPhaseEvent(get().phase, event);
    // Zustand bails out of notifying subscribers when the value is unchanged, but the
    // explicit guard also keeps ignored events from looking like state changes in devtools.
    if (next !== get().phase) set({ phase: next });
  },

  updateSettings: async (patch) => {
    const settings = parseSettings({ ...get().settings, ...patch });
    set({ settings });
    await saveSettings(settings);
  },

  hydrate: async () => {
    set({ settings: await loadSettings() });
  },

  setProfile: (profile) => {
    set({ profile, ...freshRun(profile) });
  },

  setFocus: (focus) => {
    const current = get().focus;
    // Called from the frame loop — bail out without a write unless the target changed.
    if (current?.id === focus?.id && current?.kind === focus?.kind) return;
    set({ focus });
  },

  toggleRoomLight: (roomId) => {
    const { roomLights } = get();
    const current = roomLights[roomId];
    // Unknown room: the switch belongs to no lamp, so there is nothing to flip.
    if (current === undefined) return;
    set({ roomLights: { ...roomLights, [roomId]: current === 'on' ? 'off' : 'on' } });
  },

  toggleDoor: (doorId) => {
    const { doorStates, doorwayOccupancy } = get();
    const current = doorStates[doorId] ?? 'closed';
    const next = toggleDoor(current, (doorwayOccupancy[doorId] ?? 0) > 0);
    if (next !== current) set({ doorStates: { ...doorStates, [doorId]: next } });
  },

  forceDoorOpen: (doorId) => {
    const { doorStates } = get();
    if (doorStates[doorId] === 'open') return;
    set({ doorStates: { ...doorStates, [doorId]: 'open' } });
  },

  reportDoorwayEntered: (doorId) => {
    const { doorwayOccupancy } = get();
    set({
      doorwayOccupancy: { ...doorwayOccupancy, [doorId]: (doorwayOccupancy[doorId] ?? 0) + 1 },
    });
  },

  reportDoorwayLeft: (doorId) => {
    const { doorwayOccupancy } = get();
    // Never below zero: exit events can arrive after a reset or double-fire on removal.
    set({
      doorwayOccupancy: {
        ...doorwayOccupancy,
        [doorId]: Math.max(0, (doorwayOccupancy[doorId] ?? 0) - 1),
      },
    });
  },

  pickUp: (objectId, kind) => {
    if (get().carriedObjectId !== null) return;
    set({ carriedObjectId: objectId, carriedObjectKind: kind });
  },

  release: () => {
    if (get().carriedObjectId === null) return;
    set({ carriedObjectId: null, carriedObjectKind: null });
  },

  stow: (kind) => {
    const { carriedObjectId, pocket, profile } = get();
    if (carriedObjectId === null) return;
    // A game may have no pocket (`per-game-pocket` D4). Refused here rather than left to
    // `stowInPocket`, whose empty-handed `null` the branch below reports as "Pocket full"
    // — a lie about a pocket this game does not have.
    if (!hasPocket(profile)) return;
    // A box needs both arms; only a kind that says it is stowable ever goes in.
    if (!isStowable(kind)) return;

    const next = stowInPocket(pocket, { id: carriedObjectId, kind });
    if (!next) {
      set({ itemMessage: 'Pocket full' });
      return;
    }
    set({ pocket: next, carriedObjectId: null, carriedObjectKind: null, itemMessage: null });
  },

  takeFromPocket: (slot) => {
    const { carriedObjectId, pocket } = get();
    if (carriedObjectId !== null) return;

    const taken = takeFromSlot(pocket, slot);
    if (!taken) return;
    set({
      pocket: taken.pocket,
      carriedObjectId: taken.item.id,
      carriedObjectKind: taken.item.kind,
      itemMessage: null,
    });
  },

  clearItemMessage: () => {
    if (get().itemMessage === null) return;
    set({ itemMessage: null });
  },

  attachPart: (hostId, hostKind) => {
    const { carriedObjectId, carriedObjectKind, attachments } = get();
    if (carriedObjectId === null || carriedObjectKind === null) return;
    if (!canAttach(carriedObjectKind, hostKind, attachments, hostId)) return;

    const socket = nextSocket(hostKind, attachments, hostId);
    // canAttach already checked a socket is free; this is defensive, not a real path.
    if (!socket) return;

    const nextAttachments: Attachments = {
      ...attachments,
      [carriedObjectId]: { hostId, socketId: socket.id },
    };

    const spec = ASSEMBLIES[hostKind];
    const filled = attachedTo(nextAttachments, hostId).length;
    const total = spec?.sockets.length ?? filled;
    const complete = isComplete(hostKind, nextAttachments, hostId);
    const message = complete ? 'Robot dog complete' : `Leg attached — ${filled} of ${total}`;

    set({
      attachments: nextAttachments,
      carriedObjectId: null,
      carriedObjectKind: null,
      itemMessage: message,
      // Named on the completing attach only (design D3): a `canAttach` refusal on an
      // already-full host never reaches this line, so an existing follower is never
      // overwritten by a no-op.
      ...(complete ? { followerId: hostId } : {}),
    });
  },

  beginDrag: (objectId) => {
    // One thing at a time, and never while carrying: a table needs both hands, which is
    // also the rule `availableAction` states — a full-handed press is always a drop.
    const { carriedObjectId, draggedObjectId } = get();
    if (carriedObjectId !== null || draggedObjectId !== null) return;
    set({ draggedObjectId: objectId });
  },

  endDrag: () => {
    if (get().draggedObjectId === null) return;
    set({ draggedObjectId: null });
  },

  reportZoneEvent: (objectiveId, event) => {
    const { objectiveProgress } = get();
    const progress = objectiveProgress[objectiveId];
    if (!progress) return;

    const next = applyZoneEvent(progress, event);
    if (next === progress) return;
    set({ objectiveProgress: { ...objectiveProgress, [objectiveId]: next } });
  },

  reachExit: (objectiveId) => {
    const { objectiveProgress } = get();
    const progress = objectiveProgress[objectiveId];
    if (!progress) return;

    // Already out: the second crossing is not a state change, so nothing is written.
    const next = applyPlayerEnterZone(progress);
    if (next === progress) return;
    set({ objectiveProgress: { ...objectiveProgress, [objectiveId]: next } });
  },

  beginCrossing: (destination) => {
    const { phase } = get();
    const next = applyPhaseEvent(phase, 'CROSS');
    // Already crossing, paused, or finished: the gate said so twice and the second one
    // is not an event. The phase machine is the guard, so there is only one of these.
    if (next === phase) return;
    // Written in one go with the phase, so no render ever sees a crossing with nowhere
    // to go — or a destination with the world still running.
    set({ pendingArrival: destination, phase: next });
  },

  arrive: () => {
    const { phase, pendingArrival } = get();
    const next = applyPhaseEvent(phase, 'ARRIVED');
    if (next === phase || !pendingArrival) return;
    // Hands, pocket and any attachment are empty on the far side: what the player was
    // holding, had stowed or had built belonged to the level they left, and none of it
    // is in the world any more.
    const { profile, objectiveProgress } = get();
    const destination = profile ? levelById(profile, pendingArrival.levelId) : null;
    set({
      objectiveProgress: destination
        ? seedObjectives(objectiveProgress, destination)
        : objectiveProgress,
      currentLevelId: pendingArrival.levelId,
      arriveAt: pendingArrival.arriveAt,
      pendingArrival: null,
      carriedObjectId: null,
      carriedObjectKind: null,
      draggedObjectId: null,
      pocket: emptyPocket(),
      itemMessage: null,
      attachments: {},
      followerId: null,
      focus: null,
      phase: next,
    });
  },

  restart: () => {
    const { profile, phase } = get();
    if (!profile) return;
    set({
      ...freshRun(profile),
      carriedObjectId: null,
      carriedObjectKind: null,
      draggedObjectId: null,
      pocket: emptyPocket(),
      itemMessage: null,
      attachments: {},
      followerId: null,
      focus: null,
      phase: applyPhaseEvent(phase, 'RESTART'),
    });
  },

  markUpdateReady: (applyUpdate) => {
    // The worker can report the same waiting build more than once — on registration and
    // again after a periodic check. Bail out unless something actually changed, so the
    // notice does not re-render for nothing.
    const current = get();
    if (current.isUpdateReady && current.applyUpdate === applyUpdate) return;
    set({ isUpdateReady: true, applyUpdate });
  },
}));
